import { Injectable } from '@angular/core';
import { ItemStatus } from '@enums/item-status.enum';
import { DirectusQuery } from '@interfaces/directus-query.interface';
import { DirectusSchema } from '@interfaces/directus-schema.interface';
import { Folder } from '@models/folder';
import { from, Observable } from 'rxjs';

import { DataService } from '../data/data.service';

@Injectable({
  providedIn: 'root'
})
export class FoldersService extends DataService {
  protected override collection: keyof DirectusSchema = 'folders';
  protected override fields = ['*,category.*,links.*,links.item.*,links.item.groups.groups_id.*'];

  /*
   * getItems is used to override the extendsQuery for readItems in DataService
   */
  public override getItems<T = Folder>(): Observable<T[]> {
    const query = {
      filter: {
        status: {
          _in: [ItemStatus.Published]
        }
      },
      sort: 'sort'
    };
    return super.getItems<T>(this.extendQuery(query), true);
  }

  /*
   * Method is used to search for folders by title, description, and linked item titles
   */
  public search<T = Folder>(query: string, limit = 10): Observable<T[]> {
    const searchQuery: DirectusQuery = {
      filter: {
        status: {
          _in: [ItemStatus.Published]
        },
        _or: [
          { title: { _icontains: query } },
          { description: { _icontains: query } },
          { links: { 'item:links_additional': { title: { _icontains: query } } } },
          { links: { 'item:links_conditions': { title: { _icontains: query } } } },
          { links: { 'item:links_sales_material': { title: { _icontains: query } } } }
        ]
      },
      fields: ['id', 'slug', 'title', 'links'],
      limit,
      sort: 'sort'
    };
    return from(this.directusService.readItems<T>(this.collection, this.extendQuery(searchQuery)));
  }

  /*
   * getItem is used to override the default query for getItem
   */
  public override getItem<T = Folder>(id: string, force?: boolean, isPreview?: boolean): Observable<T | null> {
    return super.getItem<T>(id, force, isPreview) as Observable<T>;
  }

  /*
   * Method is used to override the default query for readItems, depending on m2m structure for groups
   */
  protected override extendQuery(query: DirectusQuery, isPreview?: boolean): DirectusQuery {
    // set fields from current service
    query.fields = this.fields;
    // set groups from auth service, only if extendQueryWithGroups is true
    const groups = this.authService.getPermissionGroups();
    if (groups.length > 0 && !isPreview) {
      if (!query.filter) {
        query.filter = {};
      }
      const groupsOr = [
        {
          links: {
            'item:links_additional': {
              status: { _in: [ItemStatus.Published] },
              groups: { groups_id: { externalId: { _in: groups } } }
            }
          }
        },
        {
          links: {
            'item:links_conditions': {
              status: { _in: [ItemStatus.Published] },
              groups: { groups_id: { externalId: { _in: groups } } }
            }
          }
        },
        {
          links: {
            'item:links_sales_material': {
              status: { _in: [ItemStatus.Published] },
              groups: { groups_id: { externalId: { _in: groups } } }
            }
          }
        }
      ];
      if (query.filter['_or']) {
        const existingOr = query.filter['_or'];
        delete query.filter['_or'];
        query.filter['_and'] = [{ _or: existingOr }, { _or: groupsOr }];
      } else {
        query.filter['_or'] = groupsOr;
      }
      query.deep = {
        links: {
          'item:links_additional': this.getDeepFilter(groups),
          'item:links_conditions': this.getDeepFilter(groups),
          'item:links_sales_material': this.getDeepFilter(groups)
        }
      };
    }
    return query;
  }

  private getDeepFilter(groups: string[]): unknown {
    return {
      _filter: {
        status: {
          _in: [ItemStatus.Published]
        },
        groups: {
          groups_id: {
            externalId: {
              _in: groups
            }
          }
        }
      }
    };
  }
}
